/**
 * Transaction History Modal
 * Shows user's game history with stats and session drill-down
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { gameApiService, SessionSummary, RoundDetail, UserStats, PriceFeedIds } from '../../services/GameApiService';
import './Modal.css';
import './TransactionHistoryModal.css';

interface TransactionHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  userWallet: string;
}

type ViewState = 'sessions' | 'rounds';

const TransactionHistoryModal: React.FC<TransactionHistoryModalProps> = ({
  isOpen,
  onClose,
  userWallet
}) => {
  // State
  const [viewState, setViewState] = useState<ViewState>('sessions');
  const [stats, setStats] = useState<UserStats | null>(null);
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Session detail state
  const [selectedSession, setSelectedSession] = useState<SessionSummary | null>(null);
  const [rounds, setRounds] = useState<RoundDetail[]>([]);
  const [loadingRounds, setLoadingRounds] = useState(false);
  const [priceFeedIds, setPriceFeedIds] = useState<PriceFeedIds>({});
  const [expandedRoundId, setExpandedRoundId] = useState<string | null>(null);
  
  // Refs
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef(0);
  
  // Fetch initial data
  const fetchHistory = useCallback(async () => {
    if (!userWallet) return;
    
    setLoading(true);
    setError(null);
    offsetRef.current = 0;
    
    try {
      const result = await gameApiService.getUserHistory(userWallet, 20, 0);
      
      if (result.success && result.data) {
        setStats(result.data.stats);
        setSessions(result.data.sessions);
        setHasMore(result.data.hasMore);
        offsetRef.current = result.data.sessions.length;
      } else {
        setError(result.error || 'Failed to load history');
      }
    } catch (err) {
      setError('Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [userWallet]);
  
  // Load more sessions (infinite scroll)
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    
    try {
      const result = await gameApiService.getUserHistory(userWallet, 20, offsetRef.current);
      
      if (result.success && result.data) {
        setSessions(prev => [...prev, ...result.data!.sessions]);
        setHasMore(result.data.hasMore);
        offsetRef.current += result.data.sessions.length;
      }
    } catch (err) {
      console.error('Failed to load more sessions:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [userWallet, loadingMore, hasMore]);
  
  // Fetch rounds for selected session
  const fetchSessionRounds = useCallback(async (session: SessionSummary) => {
    setSelectedSession(session);
    setViewState('rounds');
    setLoadingRounds(true);
    setExpandedRoundId(null); // Reset expanded round when switching sessions
    
    try {
      const result = await gameApiService.getSessionRounds(session.session_id);
      
      if (result.success && result.data) {
        setRounds(result.data.rounds);
        setPriceFeedIds(result.data.priceFeedIds || {});
      } else {
        setRounds([]);
        setPriceFeedIds({});
      }
    } catch (err) {
      console.error('Failed to load rounds:', err);
      setRounds([]);
      setPriceFeedIds({});
    } finally {
      setLoadingRounds(false);
    }
  }, []);
  
  // Handle scroll for infinite loading
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const bottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 100;
    
    if (bottom && hasMore && !loadingMore && viewState === 'sessions') {
      loadMore();
    }
  }, [hasMore, loadingMore, loadMore, viewState]);
  
  // Reset and fetch when modal opens
  useEffect(() => {
    if (isOpen && userWallet) {
      setViewState('sessions');
      setSelectedSession(null);
      setRounds([]);
      fetchHistory();
    }
  }, [isOpen, userWallet, fetchHistory]);
  
  // Handle back button
  const handleBack = () => {
    setViewState('sessions');
    setSelectedSession(null);
    setRounds([]);
  };
  
  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  
  // Format time
  const formatTime = (dateString: string | null) => {
    if (!dateString) return '--';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };
  
  // Format SOL amount
  const formatSol = (amount: number | null) => {
    if (amount === null || amount === undefined) return '0';
    return amount.toFixed(4);
  };
  
  // Truncate session ID for display
  const truncateId = (id: string) => {
    return `${id.slice(0, 4)}...${id.slice(-4)}`;
  };
  
  // Get Solscan URL for transaction
  const getSolscanUrl = (tx: string | null) => {
    if (!tx) return null;
    // Use devnet for development, mainnet for production
    const cluster = process.env.NODE_ENV === 'production' ? '' : '?cluster=devnet';
    return `https://solscan.io/tx/${tx}${cluster}`;
  };
  
  // Calculate price change percentage
  const calcPriceChange = (startPrice: number, endPrice: number): number => {
    if (!startPrice || startPrice === 0) return 0;
    return ((endPrice - startPrice) / startPrice) * 100;
  };
  
  // Format price for display
  const formatPrice = (price: number | undefined): string => {
    if (price === undefined || price === null) return '--';
    if (price >= 1000) return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    if (price >= 1) return `$${price.toFixed(2)}`;
    return `$${price.toFixed(6)}`;
  };
  
  // Format percentage change
  const formatPctChange = (pct: number): string => {
    const sign = pct >= 0 ? '+' : '';
    return `${sign}${pct.toFixed(4)}%`;
  };
  
  // Get Pyth Benchmarks URL for verification
  const getPythVerifyUrl = (round: RoundDetail): string | null => {
    if (!round.race_end_time) return null;
    
    // Convert ISO timestamp to Unix seconds
    const timestamp = Math.floor(new Date(round.race_end_time).getTime() / 1000);
    
    // Get feed IDs for champion and challenger
    const championFeedId = priceFeedIds[round.champion];
    const challengerFeedId = priceFeedIds[round.challenger];
    
    if (!championFeedId || !challengerFeedId) return null;
    
    // Construct Pyth Benchmarks URL
    return `https://benchmarks.pyth.network/v1/updates/price/${timestamp}?ids=${championFeedId}&ids=${challengerFeedId}&parsed=true`;
  };
  
  // Toggle expanded round (accordion - only one at a time)
  const toggleRoundExpand = (roundId: string) => {
    setExpandedRoundId(prev => prev === roundId ? null : roundId);
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay history-modal-overlay" onClick={onClose}>
      <div className="history-modal" onClick={(e) => e.stopPropagation()}>
        {/* Scanline effect */}
        <div className="history-scanlines" />
        
        {/* Header */}
        <div className="history-modal-header">
          {viewState === 'rounds' && (
            <button className="history-back-button" onClick={handleBack}>
              ← Back
            </button>
          )}
          <h2 className="history-modal-title">
            {viewState === 'sessions' ? 'Past Games' : 'Session Details'}
          </h2>
          <button 
            className="history-modal-close" 
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>
        
        {/* Content */}
        <div 
          className="history-modal-content"
          ref={scrollContainerRef}
          onScroll={handleScroll}
        >
          {loading ? (
            <div className="history-loading">
              <div className="history-spinner" />
              <p>Loading history...</p>
            </div>
          ) : error ? (
            <div className="history-error">
              <p>{error}</p>
              <button onClick={fetchHistory}>Retry</button>
            </div>
          ) : viewState === 'sessions' ? (
            <>
              {/* Stats Banner */}
              {stats && (
                <div className="history-stats-banner">
                  <div className="history-stat">
                    <span className="stat-value">{stats.gamesPlayed}</span>
                    <span className="stat-label">Games</span>
                  </div>
                  <div className="history-stat">
                    <span className="stat-value">
                      <img 
                        src="/assets/icons/solanaLogoMark.svg" 
                        alt="SOL" 
                        className="history-sol-icon-stat"
                      />
                      {formatSol(stats.totalWagered)}
                    </span>
                    <span className="stat-label">Total Wagered</span>
                  </div>
                  <div className={`history-stat ${stats.netProfit >= 0 ? 'profit-positive' : 'profit-negative'}`}>
                    <span className="stat-value">
                      <img 
                        src="/assets/icons/solanaLogoMark.svg" 
                        alt="SOL" 
                        className="history-sol-icon-stat"
                      />
                      {stats.netProfit >= 0 ? '+' : ''}{formatSol(stats.netProfit)}
                    </span>
                    <span className="stat-label">Net Profit</span>
                  </div>
                  <div className="history-stat">
                    <span className="stat-value">L{stats.highestLevelWon}</span>
                    <span className="stat-label">Highest Level</span>
                  </div>
                </div>
              )}
              
              {/* Sessions List */}
              <div className="history-table-hint">💡 Click to see round details</div>
              <div className="history-sessions-header">
                <span>Date</span>
                <span>Wager</span>
                <span>Result</span>
                <span>Payout</span>
              </div>
              
              {sessions.length === 0 ? (
                <div className="history-empty">
                  <p>No completed games yet</p>
                  <span>Play some games to see your history!</span>
                </div>
              ) : (
                <div className="history-sessions-list">
                  {sessions.map((session) => (
                    <div 
                      key={session.session_id}
                      className="history-session-row"
                      onClick={() => fetchSessionRounds(session)}
                    >
                      <span className="session-date">{formatDate(session.created_at)}</span>
                      <span className="session-wager">
                        <img 
                          src="/assets/icons/solanaLogoMark.svg" 
                          alt="SOL" 
                          className="history-sol-icon"
                        />
                        {formatSol(session.initial_bet)}
                      </span>
                      <span className={`session-result ${session.session_status}`}>
                        {session.session_status === 'cashed_out' ? 'CASH' : 'BUST'}
                      </span>
                      <span className={`session-payout ${session.session_status === 'cashed_out' ? 'won' : 'lost'}`}>
                        <img 
                          src="/assets/icons/solanaLogoMark.svg" 
                          alt="SOL" 
                          className="history-sol-icon"
                        />
                        {formatSol(session.final_payout || 0)}
                      </span>
                    </div>
                  ))}
                  
                  {loadingMore && (
                    <div className="history-loading-more">
                      <div className="history-spinner-small" />
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            /* Rounds View */
            <>
              {selectedSession && (
                <div className="history-stats-banner session-detail-banner">
                  <div className="history-stat">
                    <span className="stat-value">{truncateId(selectedSession.session_id)}</span>
                    <span className="stat-label">Session</span>
                  </div>
                  <div className="history-stat">
                    <span className="stat-value">
                      <img 
                        src="/assets/icons/solanaLogoMark.svg" 
                        alt="SOL" 
                        className="history-sol-icon-stat"
                      />
                      {formatSol(selectedSession.initial_bet)}
                    </span>
                    <span className="stat-label">Wager</span>
                  </div>
                  <div className={`history-stat ${selectedSession.session_status === 'cashed_out' ? 'profit-positive' : 'profit-negative'}`}>
                    <span className="stat-value">
                      {selectedSession.session_status === 'cashed_out' ? 'WON' : 'BUST'} L{selectedSession.rounds_completed}
                    </span>
                    <span className="stat-label">Result</span>
                  </div>
                  <div className={`history-stat ${selectedSession.session_status === 'cashed_out' ? 'profit-positive' : 'profit-negative'}`}>
                    <span className="stat-value">
                      <img 
                        src="/assets/icons/solanaLogoMark.svg" 
                        alt="SOL" 
                        className="history-sol-icon-stat"
                      />
                      {formatSol(selectedSession.final_payout || 0)}
                    </span>
                    <span className="stat-label">Payout</span>
                  </div>
                </div>
              )}
              
              {/* Rounds Header */}
              <div className="history-table-hint">💡 Click to see price data</div>
              <div className="history-rounds-header">
                <span>Round</span>
                <span>Match</span>
                <span>Pick</span>
                <span>Result</span>
                <span className="time-header">Time</span>
                <span className="tx-header">Tx</span>
              </div>
              
              {loadingRounds ? (
                <div className="history-loading">
                  <div className="history-spinner" />
                  <p>Loading rounds...</p>
                </div>
              ) : rounds.length === 0 ? (
                <div className="history-empty">
                  <p>No rounds found</p>
                </div>
              ) : (
                <div className="history-rounds-list">
                  {rounds.map((round) => {
                    const isExpanded = expandedRoundId === round.round_id;
                    const championStartPrice = round.starting_prices?.[round.champion];
                    const championEndPrice = round.ending_prices?.[round.champion];
                    const challengerStartPrice = round.starting_prices?.[round.challenger];
                    const challengerEndPrice = round.ending_prices?.[round.challenger];
                    const championPctChange = championStartPrice && championEndPrice 
                      ? calcPriceChange(championStartPrice, championEndPrice) : null;
                    const challengerPctChange = challengerStartPrice && challengerEndPrice 
                      ? calcPriceChange(challengerStartPrice, challengerEndPrice) : null;
                    
                    return (
                      <div key={round.round_id} className={`history-round-item ${isExpanded ? 'expanded' : ''}`}>
                        <div 
                          className="history-round-row"
                          onClick={() => toggleRoundExpand(round.round_id)}
                        >
                          <span className="round-number">R{round.round_number}</span>
                          <span className="round-matchup">
                            {round.champion} vs {round.challenger}
                          </span>
                          <span className="round-pick">{round.player_pick || '--'}</span>
                          <span className={`round-result ${round.player_won ? 'won' : 'lost'}`}>
                            {round.player_won === null ? '--' : round.player_won ? 'W' : 'L'}
                          </span>
                          <span className="round-time">
                            <span className="time-start">{formatTime(round.race_start_time)}</span>
                            <span className="time-end">{formatTime(round.race_end_time)}</span>
                          </span>
                          <span className="round-tx">
                            {round.on_chain_bet_tx ? (
                              <a 
                                href={getSolscanUrl(round.on_chain_bet_tx) || '#'}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="tx-link-icon"
                                title="View on Solscan"
                              >
                                👁
                              </a>
                            ) : (
                              <span className="tx-empty">–</span>
                            )}
                          </span>
                        </div>
                        
                        {/* Expandable Price Verification Panel - Compact */}
                        {isExpanded && (
                          <div className="round-price-detail">
                            <div className="price-detail-rows">
                              {/* Champion Row */}
                              <div className={`price-detail-row ${round.race_winner === round.champion ? 'winner' : ''}`}>
                                <span className="price-asset">{round.champion}</span>
                                <span className="price-values">
                                  {formatPrice(championStartPrice)}→{formatPrice(championEndPrice)}
                                </span>
                                <span className={`price-change ${championPctChange && championPctChange >= 0 ? 'positive' : 'negative'}`}>
                                  {championPctChange !== null ? formatPctChange(championPctChange) : '--'}
                                </span>
                                {round.race_winner === round.champion && <span className="winner-badge">✓</span>}
                              </div>
                              
                              {/* Challenger Row */}
                              <div className={`price-detail-row ${round.race_winner === round.challenger ? 'winner' : ''}`}>
                                <span className="price-asset">{round.challenger}</span>
                                <span className="price-values">
                                  {formatPrice(challengerStartPrice)}→{formatPrice(challengerEndPrice)}
                                </span>
                                <span className={`price-change ${challengerPctChange && challengerPctChange >= 0 ? 'positive' : 'negative'}`}>
                                  {challengerPctChange !== null ? formatPctChange(challengerPctChange) : '--'}
                                </span>
                                {round.race_winner === round.challenger && <span className="winner-badge">✓</span>}
                              </div>
                            </div>
                            
                            {/* Verify link on its own row */}
                            {getPythVerifyUrl(round) && (
                              <a 
                                href={getPythVerifyUrl(round)!}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="pyth-verify-link"
                                onClick={(e) => e.stopPropagation()}
                              >
                                [Verify with Pyth ↗]
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionHistoryModal;

